import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    Divider,
    Drawer,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useFetch } from '../../../hooks/useFetch';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import useHandleErrors from '../../../hooks/useHandleErrors';
import UserTabs from '../components/UserTabs';

export default () => {
    const [addNew, setAddNew] = useState(false);
    const [editUser, setEditUser] = useState();
    const [filterView, setFilterView] = useState(false);

    // Filtros
    const [searchText, setSearchText] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterApproved, setFilterApproved] = useState('');
    const [appliedFilters, setAppliedFilters] = useState({});

    const { data, error, mutate, isFetching } = useFetch('/backoffice/users', { params: appliedFilters });
    useHandleErrors(error);

    const handleSearch = () => {
        const params = {};
        if (searchText.trim()) params.name = searchText.trim();
        if (filterType) params.type = filterType;
        if (filterType === 'anfitriao' && filterApproved) params.approved = filterApproved;
        setAppliedFilters(params);
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    const clearFilters = () => {
        setFilterType('');
        setFilterApproved('');
        setAppliedFilters(searchText.trim() ? { name: searchText.trim() } : {});
        setFilterView(false);
    };

    const applyFilters = () => {
        handleSearch();
        setFilterView(false);
    };

    const activeFilterCount = [filterType, filterApproved].filter(Boolean).length;

    const getStatusInfo = (row) => {
        if (!row.profile) return null;
        if (row.profile.approved) return { label: 'Aprovado', color: '#e8f5e9', textColor: '#2e7d32' };
        return { label: 'Pendente', color: '#fff3e0', textColor: '#e65100' };
    };

    const columns = useMemo(() => [
        {
            field: 'id',
            headerName: 'ID',
            width: 70,
        },
        {
            field: 'name',
            headerName: 'Nome',
            minWidth: 250,
            flex: 1,
        },
        {
            field: 'email',
            headerName: 'E-mail',
            minWidth: 220,
            flex: 1,
        },
        {
            field: 'meetings',
            headerName: 'Encontros',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ row }) => row.profile?.meeting?.length || 0,
        },
        {
            field: 'profile',
            headerName: 'Tipo',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ value }) => (Boolean(value) ? 'Anfitriao' : 'Conviva'),
            renderCell: ({ value }) => (
                <Chip
                    label={value}
                    size="small"
                    sx={{
                        bgcolor: value === 'Anfitriao' ? '#e6f3f1' : '#f5f5f5',
                        color: value === 'Anfitriao' ? '#006a61' : '#666',
                        fontWeight: 500,
                    }}
                />
            ),
        },
        {
            field: 'approved',
            headerName: 'Situacao',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ row }) => {
                const info = getStatusInfo(row);
                return info ? info.label : '-';
            },
            renderCell: ({ row }) => {
                const info = getStatusInfo(row);
                if (!info) return <Typography variant="caption" color="text.secondary">-</Typography>;
                return (
                    <Chip
                        label={info.label}
                        size="small"
                        sx={{
                            bgcolor: info.color,
                            color: info.textColor,
                            fontWeight: 500,
                        }}
                    />
                );
            },
        },
        {
            field: 'actions',
            headerName: '',
            width: 100,
            sortable: false,
            filterable: false,
            renderCell: ({ row }) => (
                <Button
                    variant="text"
                    onClick={() => {
                        setEditUser(row.id);
                        setAddNew(true);
                    }}
                    size="small"
                    sx={{
                        textTransform: 'none',
                        color: '#006a61',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                    }}
                >
                    Detalhes
                </Button>
            ),
        },
    ]);

    return (
        <>
            <Box sx={{
                maxWidth: 1100,
                mx: 'auto',
                p: { xs: '16px 12px 32px', sm: '24px 16px 48px' },
            }}>
                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight={600} color="#333">
                        Usuários
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Gerencie os usuários da plataforma
                    </Typography>
                </Box>

                {/* Barra de pesquisa + filtros */}
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Pesquisar por nome..."
                        value={searchText}
                        onChange={e => {
                            const val = e.target.value;
                            setSearchText(val);
                            if (!val.trim()) setAppliedFilters(prev => { const p = { ...prev }; delete p.name; return p; });
                        }}
                        onKeyDown={handleSearchKeyDown}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#999', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchText && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => { setSearchText(''); setAppliedFilters(prev => { const p = { ...prev }; delete p.name; return p; }); }}>
                                        <CloseIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                                bgcolor: 'white',
                            },
                        }}
                    />
                    <Button
                        variant="outlined"
                        onClick={handleSearch}
                        sx={{
                            minWidth: 'auto',
                            textTransform: 'none',
                            borderColor: '#006a61',
                            color: '#006a61',
                            fontWeight: 500,
                            borderRadius: '10px',
                            height: 40,
                            px: 2.5,
                            '&:hover': { borderColor: '#005a52', bgcolor: '#f0f7f6' },
                        }}
                    >
                        Buscar
                    </Button>
                    <Button
                        variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
                        onClick={() => setFilterView(true)}
                        startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
                        sx={{
                            minWidth: 'auto',
                            textTransform: 'none',
                            fontWeight: 500,
                            borderRadius: '10px',
                            height: 40,
                            px: 2,
                            whiteSpace: 'nowrap',
                            ...(activeFilterCount > 0
                                ? { bgcolor: '#006a61', '&:hover': { bgcolor: '#005a52' } }
                                : { borderColor: '#ccc', color: '#666', '&:hover': { borderColor: '#999', bgcolor: '#fafafa' } }
                            ),
                        }}
                    >
                        Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                    </Button>
                </Stack>

                {/* Tabela */}
                <Box sx={{
                    bgcolor: 'white',
                    borderRadius: '16px',
                    border: '1px solid #e8e8e8',
                    overflow: 'hidden',
                    height: 620,
                }}>
                    <DataGrid
                        paginationMode="server"
                        loading={isFetching}
                        autoPageSize
                        rows={data || []}
                        columns={columns}
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-columnHeaders': {
                                bgcolor: '#fafafa',
                                borderBottom: '1px solid #e8e8e8',
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                color: '#555',
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #f5f5f5',
                            },
                            '& .MuiDataGrid-row:hover': {
                                bgcolor: '#f9fffe',
                            },
                            '& .MuiDataGrid-footerContainer': {
                                display: 'none',
                            },
                        }}
                    />
                </Box>
            </Box>

            {/* Dialog de detalhes */}
            <Dialog
                fullWidth
                maxWidth="sm"
                open={addNew}
                scroll="paper"
                onClose={() => setAddNew(false)}
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600, pr: 5 }}>
                    Dados do usuário
                    <IconButton
                        onClick={() => setAddNew(false)}
                        sx={{ position: 'absolute', right: 8, top: 8, color: '#999' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Divider />
                {editUser && <UserTabs mutate={mutate} user_id={editUser} close={() => { setEditUser(); setAddNew(false); }} />}
            </Dialog>

            {/* Drawer de filtros */}
            <Drawer
                anchor="right"
                variant="temporary"
                open={filterView}
                onClose={() => setFilterView(false)}
                PaperProps={{ sx: { borderRadius: '16px 0 0 16px' } }}
            >
                <Box sx={{ width: 320, p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        <Typography variant="h6" fontWeight={600} color="#333">
                            Filtros
                        </Typography>
                        <IconButton onClick={() => setFilterView(false)} size="small">
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Stack>

                    <Stack spacing={2.5}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Tipo de usuário</InputLabel>
                            <Select
                                value={filterType}
                                label="Tipo de usuário"
                                onChange={e => {
                                    setFilterType(e.target.value);
                                    if (e.target.value !== 'anfitriao') setFilterApproved('');
                                }}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="conviva">Conviva</MenuItem>
                                <MenuItem value="anfitriao">Anfitrião</MenuItem>
                            </Select>
                        </FormControl>

                        {filterType === 'anfitriao' && (
                            <FormControl fullWidth size="small">
                                <InputLabel>Situação</InputLabel>
                                <Select
                                    value={filterApproved}
                                    label="Situação"
                                    onChange={e => setFilterApproved(e.target.value)}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="true">Aprovados</MenuItem>
                                    <MenuItem value="false">Pendentes</MenuItem>
                                </Select>
                            </FormControl>
                        )}

                        <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                            <Button
                                variant="contained"
                                onClick={applyFilters}
                                fullWidth
                                sx={{
                                    bgcolor: '#006a61',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    '&:hover': { bgcolor: '#005a52' },
                                }}
                            >
                                Aplicar
                            </Button>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={clearFilters}
                                sx={{
                                    textTransform: 'none',
                                    borderColor: '#ccc',
                                    color: '#666',
                                    borderRadius: '8px',
                                }}
                            >
                                Limpar
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Drawer>
        </>
    );
};
